<template>
    <div class="meeting-formats">
        <div class="formats-header">
            <h1>Meeting Formats</h1>
            <button class="add-format-btn" @click="showCreateModal = true">
                <i class="fas fa-plus desktop-only"></i>
                <span>Add Format</span>
            </button>
        </div>

        <!-- Formats List -->
        <div class="formats-table">
            <table>
                <thead>
                    <tr>
                        <th @click="sortBy('name')" class="sortable">
                            Name
                            <span class="sort-icon" v-if="sortField === 'name'">{{ sortDirection === 'asc' ? '↓' : '↑' }}</span>
                        </th>
                        <th @click="sortBy('default_duration_minutes')" class="sortable">
                            Duration
                            <span class="sort-icon" v-if="sortField === 'default_duration_minutes'">{{ sortDirection === 'asc' ? '↓' : '↑' }}</span>
                        </th>
                        <th @click="sortBy('segments')" class="sortable">
                            Segments
                            <span class="sort-icon" v-if="sortField === 'segments'">{{ sortDirection === 'asc' ? '↓' : '↑' }}</span>
                        </th>
                        <th @click="sortBy('is_active')" class="sortable">
                            Status
                            <span class="sort-icon" v-if="sortField === 'is_active'">{{ sortDirection === 'asc' ? '↓' : '↑' }}</span>
                        </th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="format in sortedFormats" 
                        :key="format.id"
                        class="format-row"
                    >
                        <td>{{ format.name }}</td>
                        <td>{{ format.default_duration_minutes }} minutes</td>
                        <td>{{ format.segments?.length || 0 }} segments</td>
                        <td>
                            <div class="status-badge" :class="{ 'active': format.is_active }">
                                {{ format.is_active ? 'Active' : 'Inactive' }}
                            </div>
                        </td>
                        <td class="actions-cell">
                            <div class="dropdown" :class="{ active: activeDropdown === format.id }">
                                <button class="btn-icon" @click="toggleDropdown(format)">
                                    <i class="material-icons">more_vert</i>
                                </button>
                                <div class="dropdown-menu" v-if="activeDropdown === format.id">
                                    <button class="dropdown-item" @click="editFormat(format)">
                                        <i class="material-icons">edit</i>
                                        Edit
                                    </button>
                                    <button class="dropdown-item" @click="cloneFormat(format)">
                                        <i class="material-icons">content_copy</i>
                                        Clone
                                    </button>
                                    <button class="dropdown-item" @click="toggleFormatStatus(format)">
                                        <i class="material-icons">{{ format.is_active ? 'visibility_off' : 'visibility' }}</i>
                                        {{ format.is_active ? 'Deactivate' : 'Activate' }}
                                    </button>
                                </div>
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Create/Edit Modal -->
        <FormatModal
            v-if="showCreateModal || showEditModal"
            :format="selectedFormat"
            :isEdit="showEditModal"
            @save="saveFormat"
            @close="closeModal"
        />
    </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { meetingFormatService } from '../services/meetingFormatService';
import FormatModal from './FormatModal.vue';

export default {
    name: 'MeetingFormatManagement',
    components: {
        FormatModal,
    },

    setup() {
        const formats = ref([]);
        const sortField = ref('name');
        const sortDirection = ref('asc');
        const showCreateModal = ref(false);
        const showEditModal = ref(false);
        const selectedFormat = ref(null);
        const activeDropdown = ref(null);

        // Fetch formats on component mount
        onMounted(async () => {
            try {
                formats.value = await meetingFormatService.getAllFormats();
            } catch (error) {
                console.error('Error fetching formats:', error);
                // TODO: Show error toast
            }
        });

        const sortedFormats = computed(() => {
            let sorted = [...formats.value];
            if (sortField.value) {
                sorted.sort((a, b) => {
                    let aValue, bValue;
                    
                    switch (sortField.value) {
                        case 'name':
                            aValue = a.name.toLowerCase();
                            bValue = b.name.toLowerCase();
                            break;
                        case 'default_duration_minutes':
                            aValue = a.default_duration_minutes;
                            bValue = b.default_duration_minutes;
                            break;
                        case 'segments':
                            aValue = a.segments?.length || 0;
                            bValue = b.segments?.length || 0;
                            break;
                        case 'is_active':
                            aValue = a.is_active ? 1 : 0;
                            bValue = b.is_active ? 1 : 0;
                            break;
                        default:
                            return 0;
                    }
                    
                    const modifier = sortDirection.value === 'asc' ? 1 : -1;
                    if (aValue < bValue) return -1 * modifier;
                    if (aValue > bValue) return 1 * modifier;
                    return 0;
                });
            }
            return sorted;
        });

        const sortBy = (field) => {
            if (sortField.value === field) {
                sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc';
            } else {
                sortField.value = field;
                sortDirection.value = 'asc';
            }
        };

        const toggleDropdown = (format) => {
            activeDropdown.value = activeDropdown.value === format.id ? null : format.id;
        };

        const formatDuration = (minutes) => {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            return `${hours}h ${remainingMinutes}m`;
        };

        const editFormat = (format) => {
            selectedFormat.value = { ...format };
            showEditModal.value = true;
            activeDropdown.value = null;
        };

        const cloneFormat = async (format) => {
            try {
                const newFormat = await meetingFormatService.cloneFormat(format.id);
                formats.value.push(newFormat);
                activeDropdown.value = null;
            } catch (error) {
                console.error('Error cloning format:', error);
                // TODO: Show error toast
            }
        };

        const toggleFormatStatus = async (format) => {
            try {
                await meetingFormatService.toggleFormat(format.id);
                format.is_active = !format.is_active;
                activeDropdown.value = null;
            } catch (error) {
                console.error('Error toggling format status:', error);
                // TODO: Show error toast
            }
        };

        const saveFormat = async (formatData) => {
            try {
                if (showEditModal.value) {
                    await meetingFormatService.updateFormat(selectedFormat.value.id, formatData);
                    const index = formats.value.findIndex(f => f.id === selectedFormat.value.id);
                    formats.value[index] = { ...formats.value[index], ...formatData };
                } else {
                    const newFormat = await meetingFormatService.createFormat(formatData);
                    formats.value.push(newFormat);
                }
                closeModal();
            } catch (error) {
                console.error('Error saving format:', error);
                // TODO: Show error toast
            }
        };

        const closeModal = () => {
            showCreateModal.value = false;
            showEditModal.value = false;
            selectedFormat.value = null;
        };

        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (activeDropdown.value && !event.target.closest('.dropdown')) {
                activeDropdown.value = null;
            }
        };

        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
        });

        return {
            formats,
            sortedFormats,
            sortField,
            sortDirection,
            showCreateModal,
            showEditModal,
            selectedFormat,
            activeDropdown,
            sortBy,
            toggleDropdown,
            formatDuration,
            editFormat,
            cloneFormat,
            toggleFormatStatus,
            saveFormat,
            closeModal,
        };
    },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/icon?family=Material+Icons');

.meeting-formats {
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
}

.formats-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding: 0 16px;
}

.formats-header h1 {
    margin: 0;
    font-size: 24px;
}

.add-format-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #4CAF50;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.2s;
}

.add-format-btn:hover {
    background-color: #45a049;
}

.desktop-only {
    display: inline-block;
}

@media (max-width: 768px) {
    .desktop-only {
        display: none;
    }

    .formats-header {
        padding: 0 12px;
    }

    .add-format-btn {
        padding: 6px 12px;
        font-size: 13px;
    }
}

.formats-table {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    overflow: visible;
    position: relative;
    z-index: 1;
}

table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
}

th, td {
    text-align: left !important;
    padding: 1rem;
}

th {
    background: #f8f9fa;
    font-weight: 500;
    color: #2c3e50;
    border-bottom: 2px solid #eee;
}

td {
    border-bottom: 1px solid #eee;
    vertical-align: middle;
}

tr:last-child td {
    border-bottom: none;
}

.sortable {
    cursor: pointer;
}

.sort-icon {
    margin-left: 4px;
    opacity: 0.5;
}

.status-badge {
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    background-color: #eee;
    color: #666;
}

.status-badge.active {
    background-color: #e8f5e9;
    color: #2e7d32;
}

.actions-cell {
    width: 1%;
    white-space: nowrap;
    text-align: left;
}

.dropdown {
    position: relative;
    display: inline-block;
}

.dropdown.active .dropdown-menu {
    display: block;
}

.dropdown-menu {
    display: none;
    position: absolute;
    top: 100%;
    right: 0;
    background-color: #fff;
    border: 1px solid #ddd;
    padding: 0.5rem;
    z-index: 1;
    min-width: 160px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    text-align: left;
}

.dropdown-item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 16px;
    border: none;
    background: none;
    text-align: left;
    font-size: 0.875rem;
    cursor: pointer;
}

.dropdown-item:hover {
    background-color: #f5f5f5;
}

.btn-icon {
    padding: 4px;
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
}

.material-icons {
    font-size: 18px;
}

@media (max-width: 768px) {
    .formats-table {
        overflow-x: auto;
    }
}
</style>
